const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strong',
  'u',
  'ul',
]);

const ALLOWED_ATTRS = {
  a: new Set(['href', 'target', 'rel']),
  '*': new Set(['class']),
};

function isSafeUrl(value) {
  const normalized = value.trim().toLowerCase();
  return normalized.startsWith('http://')
    || normalized.startsWith('https://')
    || normalized.startsWith('mailto:')
    || normalized.startsWith('tel:')
    || normalized.startsWith('/')
    || normalized.startsWith('#');
}

function sanitizeNode(node, doc) {
  if (node.nodeType === Node.TEXT_NODE) {
    return doc.createTextNode(node.textContent || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return doc.createTextNode('');
  }

  const tagName = node.tagName.toLowerCase();
  if (!ALLOWED_TAGS.has(tagName)) {
    const fragment = doc.createDocumentFragment();
    Array.from(node.childNodes).forEach(child => {
      fragment.appendChild(sanitizeNode(child, doc));
    });
    return fragment;
  }

  const cleanNode = doc.createElement(tagName);
  const allowedAttrs = new Set([
    ...(ALLOWED_ATTRS['*'] || []),
    ...(ALLOWED_ATTRS[tagName] || []),
  ]);

  Array.from(node.attributes).forEach(attr => {
    const attrName = attr.name.toLowerCase();
    if (!allowedAttrs.has(attrName)) {
      return;
    }

    if (tagName === 'a' && attrName === 'href' && !isSafeUrl(attr.value)) {
      return;
    }

    cleanNode.setAttribute(attrName, attr.value);
  });

  if (tagName === 'a' && cleanNode.hasAttribute('target') && !cleanNode.hasAttribute('rel')) {
    cleanNode.setAttribute('rel', 'noopener noreferrer');
  }

  Array.from(node.childNodes).forEach(child => {
    cleanNode.appendChild(sanitizeNode(child, doc));
  });

  return cleanNode;
}

export function sanitizeHtml(html = '') {
  if (!html || typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html || '';
  }

  const parser = new DOMParser();
  const parsed = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const wrapper = parsed.body.firstElementChild;

  if (!wrapper) {
    return '';
  }

  const cleanWrapper = parsed.createElement('div');
  Array.from(wrapper.childNodes).forEach(child => {
    cleanWrapper.appendChild(sanitizeNode(child, parsed));
  });

  return cleanWrapper.innerHTML;
}

export function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
